
import { defineComponent, ComponentPublicInstance, nextTick } from 'vue';
import { gsap } from 'gsap';
import Draggable, { Draggable as DraggableInstance } from 'gsap/Draggable';
import Swiper from 'swiper';
import BizIcon from '@/components/base/BizIcon.vue';

import { getTwConfig, debounce } from '@/utils';

gsap.registerPlugin(Draggable);

export default defineComponent({
    components: { BizIcon },
    props: {
        editMode: {
            type: Boolean,
            default: false
        },
        shuffleText: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            active: false,
            items: [] as ComponentPublicInstance[], // currently displayed items
            allItems: [] as ComponentPublicInstance[], // store all items for rotation
            visibleItems: [] as ComponentPublicInstance[], // currently displayed items (max 5)
            draggables: [] as DraggableInstance[],
            twConfig: getTwConfig(),
            randomized: false,
            swiper: null as Swiper | null,
            isDesktop: false,
            breakpoint: 1024,
            resizeHandlerDebounce: null,
            hasRevealed: false, // animation already played
            scrollHandler: null,
            currentIndex: 0, // for slider
            maxVisibleItems: 10, // maximum items to show at once
            reduceVisibleItems: 5 // maximum items to show at once
        };
    },

    provide() {
        return {
            registerPin: (pin: ComponentPublicInstance) => this.registerPin(pin)
        };
    },

    async mounted() {
        await nextTick();

        this.breakpoint = this.breakpointSize('lg');
        this.isDesktop = window.innerWidth > this.breakpoint;

        if (this.editMode) return;

        if (!this.randomized && this.items.length > 0) {
            this.randomizePins();
            this.randomized = true;
        }

        if (this.isDesktop) {
            await this.initDraggables();

            this.scrollHandler = this.checkCenterTrigger.bind(this);
            window.addEventListener('scroll', this.scrollHandler, { passive: true });
            // check immediately in case already centered
            this.checkCenterTrigger();
        } else {
            await this.initSwiper();
        }

        this.resizeHandlerDebounce = debounce(this.handleResize.bind(this), 150);
        window.addEventListener('resize', this.resizeHandlerDebounce);
    },

    beforeUnmount() {
        if (this.editMode) return;
        if (this.resizeHandlerDebounce) {
            window.removeEventListener('resize', this.resizeHandlerDebounce);
        }

        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
            this.scrollHandler = null;
        }

        this.destroyOlds();
    },

    methods: {
        registerPin(pin: ComponentPublicInstance) {
            this.allItems.push(pin);
            this.items.push(pin);
        },

        breakpointSize(name: string): number {
            if (Object.prototype.hasOwnProperty.call(this.twConfig.theme.screens, name)) {
                return parseInt(this.twConfig.theme.screens[name]);
            }
            return -1;
        },

        handleResize() {
            if (this.editMode) return;
            const nowDesktop = window.innerWidth > this.breakpoint;

            if (nowDesktop === this.isDesktop) return;

            // clear any existing scroll listener
            if (this.scrollHandler) {
                window.removeEventListener('scroll', this.scrollHandler);
                this.scrollHandler = null;
            }

            this.isDesktop = nowDesktop;
            this.hasRevealed = false;

            // Destroy old instances first
            this.destroyOlds();

            if (this.isDesktop) {
                // Wait for DOM to settle after destroying swiper
                nextTick(() => {
                    this.initDraggables();

                    this.scrollHandler = this.checkCenterTrigger.bind(this);
                    window.addEventListener('scroll', this.scrollHandler, { passive: true });
                    // in case it already sits at center
                    this.checkCenterTrigger();
                });
            } else {
                // Wait for DOM to settle after destroying draggables
                nextTick(() => {
                    this.initSwiper();
                });
            }
        },

        randomizePins() {
            if (this.editMode) return;
            const container = this.$refs.container as HTMLElement | undefined;
            if (!container) return;

            const wrapper = container.querySelector('.pinboard-items') as HTMLElement | null;
            if (!wrapper) return;

            // Select random items if we have more than maxVisibleItems
            let itemsToShow = this.allItems;
            if (this.allItems.length > this.maxVisibleItems) {
                // Shuffle all items and take only reduceVisibleItems
                const shuffledAll = [...this.allItems].sort(() => Math.random() - 0.5);
                itemsToShow = shuffledAll.slice(0, this.reduceVisibleItems);
            }

            // Hide all items first (only in desktop mode)
            if (this.isDesktop) {
                this.allItems.forEach(pin => {
                    const el = (pin as any).$el as HTMLElement | undefined;
                    if (el) {
                        el.style.display = 'none';
                    }
                });
            }

            // Shuffle the selected items for positioning
            const shuffled = [...itemsToShow].sort(() => Math.random() - 0.5);

            shuffled.forEach((pin, index) => {
                const el = (pin as any).$el as HTMLElement | undefined;
                if (!el) return;

                // Show this item (only in desktop mode)
                if (this.isDesktop) {
                    el.style.display = '';
                }

                // reorder at DOM for mobile
                wrapper.appendChild(el);

                // Store z-index as data attribute for later reference
                el.dataset.desktopZIndex = String(index + 1);

                // last without any rotation for desktop
                if (index === shuffled.length - 1) {
                    (pin as any).rotation = 0;
                }
            });

            // Update visible items and items array
            this.visibleItems = shuffled;
            this.items = shuffled;
        },

        async initDraggables() {
            this.destroyOlds();
            if (this.editMode) return;

            const container = this.$refs.container as HTMLElement;
            if (!container) return;

            await nextTick();

            this.items.forEach((pin, index) => {
                const el = (pin as any).$el as HTMLElement;
                if (!el) return;

                // Apply desktop z-index
                const zIndex = el.dataset.desktopZIndex || String(index + 1);
                el.style.zIndex = zIndex;

                const instances = Draggable.create(el, {
                    bounds: container,
                    cursor: 'inherit',
                    activeCursor: 'inherit'
                });

                instances.forEach(instance => {
                    this.draggables.push(instance);
                });
            });
        },

        async initSwiper() {
            this.destroyOlds();
            if (this.editMode) return;

            const container = this.$refs.container as HTMLElement;
            if (!container) return;

            await nextTick();

            // Reset all items before initializing swiper
            this.items.forEach(pin => {
                const el = (pin as any).$el as HTMLElement;
                if (!el) return;

                // Remove desktop z-index
                el.style.removeProperty('z-index');
                // Remove any rotation applied by desktop
                el.style.removeProperty('rotate');
                el.style.removeProperty('opacity');
            });

            this.swiper = new Swiper(container, {
                slidesPerView: 1,
                on: {
                    init: () => {
                        this.currentIndex = this.swiper ? this.swiper.activeIndex : 0;
                    },
                    slideChange: () => {
                        this.currentIndex = this.swiper ? this.swiper.activeIndex : 0;
                    }
                }
            });
        },

        destroyOlds() {
            // Destroy Swiper instance
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }

            // Reset inline styles (use visibleItems instead of items)
            this.visibleItems.forEach(pin => {
                const el = (pin as any).$el as HTMLElement;
                if (!el) return;

                // Remove all inline styles applied by GSAP/Draggable
                el.style.removeProperty('transform');
                el.style.removeProperty('touch-action');
                el.style.removeProperty('user-select');
                el.style.removeProperty('pointer-events');
                el.style.removeProperty('z-index');
                el.style.removeProperty('scale');
                el.style.removeProperty('opacity');
                el.style.removeProperty('rotate');
            });

            // Destroy active Draggable instances
            this.draggables.forEach(d => d.kill());
            this.draggables = [];
        },

        isInViewportCenter(): boolean {
            if (this.editMode) return false;
            const container = this.$refs.container as HTMLElement;
            if (!container) return false;

            const rect = container.getBoundingClientRect();
            const viewportCenter = window.innerHeight / 2;
            const elementCenter = rect.top + rect.height / 2;
            const triggerRange = 80;

            return Math.abs(elementCenter - viewportCenter) <= triggerRange;
        },

        checkCenterTrigger() {
            if (this.editMode) return;
            if (this.hasRevealed) return;
            if (!this.isDesktop) return;

            if (this.isInViewportCenter()) {
                this.animateDesktopReveal();
                this.hasRevealed = true;

                if (this.scrollHandler) {
                    window.removeEventListener('scroll', this.scrollHandler);
                    this.scrollHandler = null;
                }
            }
        },

        animateDesktopReveal() {
            if (this.editMode) return;
            if (!this.isDesktop) return;
            if (this.hasRevealed) return;
            if (!this.items.length) return;

            const elements = this.items.map(pin => (pin as any).$el as HTMLElement);

            // initial state
            gsap.set(elements, {
                scale: 0.85,
                opacity: 0,
                y: 20,
                transformOrigin: '50% 50%'
            });
            // smooth staggered reveal
            gsap.to(elements, {
                scale: 1,
                opacity: 1,
                y: 0,
                ease: 'power2.out',
                duration: 0.5,
                stagger: 0.3,
                onComplete: () => {
                    this.hasRevealed = true;
                }
            });
        },

        async shuffleItems() {
            if (!this.isDesktop || this.editMode) return;

            const elements = this.items.map(pin => (pin as any).$el as HTMLElement);

            // Animate out - make pins fly away
            await gsap.to(elements, {
                scale: 0,
                opacity: 0,
                y: -100,
                ease: 'power2.in',
                duration: 0.4,
                stagger: 0.05
            });

            // Reorganize
            this.randomizePins();

            // Reset positions to center before animating in
            const newElements = this.items.map(pin => (pin as any).$el as HTMLElement);
            newElements.forEach(el => {
                if (!el) return;
                // Reset only the translate3d part to center, keeping rotate
                gsap.set(el, {
                    x: 0,
                    y: 0,
                    force3D: true
                });
            });

            // Reset hasRevealed to allow animation again
            this.hasRevealed = false;

            // Re-initialize draggables with new order
            await this.initDraggables();

            // Animate in with the reveal animation
            this.animateDesktopReveal();
        }
    }
});
